import React from "react"
import { Box, Text, useDisclosure } from "@chakra-ui/react"
import { graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { Lightbox, ServiceText, LinksAndDownloads } from "../../components"

const Image = ({ image }) => {
  const parsedImage = getImage(image.bild?.localFile)
  const { isOpen, onOpen, onClose } = useDisclosure()

  const Img = ({ inlineText }) => (
    <GatsbyImage
      image={parsedImage}
      alt={image.bild?.altText || image.bild?.title}
      style={{
        width: "100%",
        height: "100%",
      }}
      imgStyle={{
        objectFit: "contain",
        objectPosition: inlineText && "left",
      }}
    />
  )

  return (
    <>
      <Box
        mb={12}
        mt={8}
        ml={{ base: 0, lg: 16 }}
        mr={{ base: 0, lg: 8 }}
        cursor={image.oeffnenInLightbox && "pointer"}
        onClick={image.oeffnenInLightbox && onOpen}
        pr={image.bildNichtVolleBreite && { md: "15%" }}
      >
        <Img inlineText />
        {image.bildtext && (
          <Text
            fontFamily="CocoSharpL-Bold"
            fontSize="0.9735rem"
            lineHeight="1.25rem"
            mt={4}
          >
            {image.bildtext}
          </Text>
        )}
        {image.bildquelle && (
          <Text
            fontFamily="CocoSharpL-Regular"
            fontSize="0.9735rem"
            lineHeight="1.25rem"
            mt={!image.bildtext ? 4 : 3}
          >
            Quelle: {image.bildquelle}
          </Text>
        )}
      </Box>
      {image.oeffnenInLightbox && (
        <Lightbox isOpen={isOpen} onClose={onClose}>
          <Img />
        </Lightbox>
      )}
    </>
  )
}

export const Paragraph = ({ text, isBlickePage }) => {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: text }}
      fontFamily={isBlickePage && "kepler-std-caption"}
      fontSize={isBlickePage ? "1.1875rem" : "1rem"}
      lineHeight="1.3125rem"
      // whiteSpace="initial"
      className={isBlickePage ? "blicke" : null}
      ml={{ base: 0, lg: 16 }}
      mr={{ base: 0, lg: 8 }}
      sx={textStyles}
    />
  )
}

const Zitat = ({ zitat }) => {
  if (zitat) {
    return (
      <>
        <Box as="figure" my={10} mr={{ base: 0, lg: 8 }}>
          <Text
            as="blockquote"
            fontFamily="kepler-std-subhead"
            fontWeight="bold"
            fontSize="1.5rem"
            lineHeight="1.5rem"
          >
            {zitat.text}
            {zitat.name && (
              <Text as="span" fontSize="lg">
                {" "}
                —{" "}
              </Text>
            )}
            <Text
              as="figcaption"
              display="inline"
              fontFamily="CocoSharpL-Bold"
              fontSize="1rem"
              fontWeight="normal"
              lineHeight="1.5rem"
            >
              {zitat.name && zitat.name}{" "}
              <Text
                as="span"
                fontFamily="CocoSharpL-Regular"
                fontSize="1rem"
                lineHeight="1.5rem"
                fontWeight="normal"
              >
                {zitat.nameBeschreibung && `/ ${zitat.nameBeschreibung}`}
              </Text>
            </Text>
          </Text>
        </Box>
      </>
    )
  }
  return null
}

const Content = ({
  content,
  bottomElemente,
  linksAndDownloads,
  isBlickePage = false,
}) => {
  return (
    <Box
      pl={{ base: null, lg: 4 }}
      maxW={{ base: null, lg: "900px" }}
      pr={{ base: null, lg: 16 }}
      mb={16}
    >
      {content.map((item, index) => {
        switch (item.fieldGroupName) {
          case "Page_Pagebuilder_FlexibleInhalte_Text":
            return (
              <Paragraph
                isBlickePage={isBlickePage}
                key={`${item.fieldGroupName}+${index}`}
                text={item.text}
              />
            )
          case "Page_Pagebuilder_FlexibleInhalte_Bild":
            return (
              <Image key={`${item.fieldGroupName}+${index}`} image={item} />
            )
          case "Page_Pagebuilder_FlexibleInhalte_Zitat":
            return (
              <Zitat
                key={`${item.fieldGroupName}+${index}`}
                zitat={item.zitat}
              />
            )
          default:
            return null
        }
      })}
      {bottomElemente?.serviceText && (
        <ServiceText data={bottomElemente.serviceText} />
      )}
      {(linksAndDownloads?.downloadsBottom ||
        linksAndDownloads?.weiterfuehrendeLinks ||
        linksAndDownloads?.kontakt) && (
        <LinksAndDownloads data={linksAndDownloads} />
      )}
    </Box>
  )
}

export default Content

export const query = graphql`
  fragment PageTemplateInhalteFragment on WpPage_Pagebuilder {
    flexibleInhalte {
      ... on WpPage_Pagebuilder_FlexibleInhalte_Bild {
        bildquelle
        bildtext
        oeffnenInLightbox
        bildNichtVolleBreite
        bild {
          altText
          title
          localFile {
            name
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        fieldGroupName
      }
      ... on WpPage_Pagebuilder_FlexibleInhalte_Text {
        fieldGroupName
        text
      }
      ... on WpPage_Pagebuilder_FlexibleInhalte_Zitat {
        fieldGroupName
        zitat {
          name
          nameBeschreibung
          text
        }
      }
    }
  }
  fragment PageTemplateServiceTextFragment on WpPage_Pagebuilder {
    fixeElementeBottom {
      serviceText
    }
  }
  fragment PageTemplateLinksAndDownloadsFragment on WpPage_Pagebuilder {
    weiterfuehrendeLinksUndDownloads {
      kontakt
      downloadsBottom {
        downloadLinkText
        downloadLinkDatei {
          localFile {
            publicURL
          }
        }
      }
      weiterfuehrendeLinks {
        link
        linkText
      }
    }
  }
`

export const textStyles = {
  "p, ul, ol, h1, h2": {
    marginBottom: "1rem",
  },
  table: {
    borderCollapse: "collapse",
    borderStyle: "hidden",
    marginBottom: "2rem",
    overflowX: "scroll",
    width: "100%",
    maxWidth: "fit-content",
    display: "block",
  },
  "table td": {
    border: "1px solid #002149",
    padding: "0.5rem",
    fontFamily: "CocoSharpL-Regular",
    fontSize: "0.9375rem",
    lineHeight: "1.25rem",
    color: "var(--chakra-colors-dark_blue)",
  },
  "table tr:first-of-type td": {
    fontFamily: "CocoSharpL-Bold",
    fontSize: "0.9375rem",
    lineHeight: "1.25rem",
  },
  "&.blicke:first-of-type p:first-of-type:first-letter": {
    float: "left",
    fontSize: "4.7rem",
    fontFamily: "kepler-std-display",
    fontWeight: "bold",
    lineHeight: "3rem",
    paddingTop: "0.6rem",
    paddingRight: "0.5rem",
    paddingLeft: "0.1875rem",
  },
  "strong, b": {
    fontFamily: "CocoSharpL-Bold",
    fontWeight: "normal",
  },
  em: {
    fontFamily: "CocoSharpL-Italic",
    fontStyle: "normal",
  },
  "&.blicke strong": {
    fontFamily: "kepler-std-caption",
    fontWeight: "bold",
  },
  "&.blicke em": {
    fontFamily: "kepler-std-caption",
    fontStyle: "italic",
  },
  a: {
    textDecoration: "underline",
  },
  "h1, h2, h3, h4, h5, h6": {
    color: "var(--chakra-colors-dark_blue)",
    fontFamily: "CocoSharpL-Bold",
  },

  "h1, h2": {
    fontSize: "1.25rem",
    lineHeight: "1.3125rem",
    marginBottom: "0.9rem",
  },

  "h3, h4, h5, h6": {
    fontSize: "0.9375rem",
    lineHeight: "1.25rem",
    marginBottom: "0.2rem",
  },
  li: {
    marginBottom: "0.75rem",
  },
}
